'use client';
import React, { useState, useRef } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import {
  Zap, MessageSquare, Info, X, Plus, GripVertical, ArrowDown, Trash2, CheckCircle, Tag,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────
interface ActionBlock {
  id: string;
  type: 'enroll_campaign' | 'send_template' | 'no_action';
  label: string;
  value: string;
  color: string;
  icon: React.ElementType;
  bgColor: string;
  borderColor: string;
  textColor: string;
}

interface FlowOption {
  id: string;
  optionNumber: number;
  optionLabel: string;
  keywords: string[];
  assignedAction: ActionBlock | null;
}

interface DragDropFlowBuilderProps {
  onClose: () => void;
  onApply: (options: Array<{
    option_number: number;
    option_label: string;
    action_type: 'enroll_campaign' | 'send_template' | 'none';
    campaign_name: string;
    template_name: string;
    keywords: string[];
  }>) => void;
}

// ─── Palette Action Blocks ────────────────────────────────────────────────────
const PALETTE_BLOCKS: ActionBlock[] = [
  {
    id: 'enroll_campaign',
    type: 'enroll_campaign',
    label: 'Enroll Campaign',
    value: '',
    color: 'text-blue-700',
    icon: Zap,
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-300',
    textColor: 'text-blue-700',
  },
  {
    id: 'send_template',
    type: 'send_template',
    label: 'Send Template',
    value: '',
    color: 'text-emerald-700',
    icon: MessageSquare,
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-300',
    textColor: 'text-emerald-700',
  },
  {
    id: 'no_action',
    type: 'no_action',
    label: 'No Action',
    value: '',
    color: 'text-gray-500',
    icon: Info,
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-300',
    textColor: 'text-gray-500',
  },
];

const OPTION_COLORS = [
  { bg: 'bg-violet-50', border: 'border-violet-200', badge: 'bg-violet-600', text: 'text-violet-700' },
  { bg: 'bg-sky-50', border: 'border-sky-200', badge: 'bg-sky-600', text: 'text-sky-700' },
  { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-500', text: 'text-amber-700' },
  { bg: 'bg-rose-50', border: 'border-rose-200', badge: 'bg-rose-500', text: 'text-rose-700' },
  { bg: 'bg-teal-50', border: 'border-teal-200', badge: 'bg-teal-600', text: 'text-teal-700' },
];

function getOptionColor(index: number) {
  return OPTION_COLORS[index % OPTION_COLORS.length];
}

// ─── Draggable Palette Block ──────────────────────────────────────────────────
function DraggablePaletteBlock({ block }: { block: ActionBlock }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `palette-${block.id}`,
    data: { type: 'palette', block },
  });

  const style = transform
    ? { transform: CSS.Translate.toString(transform), opacity: isDragging ? 0.4 : 1 }
    : undefined;

  const Icon = block.icon;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border-2 ${block.bgColor} ${block.borderColor} cursor-grab active:cursor-grabbing select-none transition-shadow hover:shadow-md`}
    >
      <GripVertical size={13} className="text-gray-400 shrink-0" />
      <Icon size={15} className={block.textColor} />
      <span className={`text-xs font-semibold ${block.textColor}`}>{block.label}</span>
    </div>
  );
}

// ─── Drop Zone (Option Slot) ──────────────────────────────────────────────────
function OptionDropZone({
  option,
  index,
  onRemoveAction,
  onUpdateValue,
  isOver,
}: {
  option: FlowOption;
  index: number;
  onRemoveAction: (optionId: string) => void;
  onUpdateValue: (optionId: string, value: string) => void;
  isOver: boolean;
}) {
  const color = getOptionColor(index);
  const action = option.assignedAction;

  return (
    <div className={`rounded-xl border-2 ${color.border} ${color.bg} overflow-hidden`}>
      {/* Option header */}
      <div className="flex items-center gap-2.5 px-3 py-2.5">
        <span className={`w-6 h-6 rounded-full ${color.badge} text-white text-[10px] font-bold flex items-center justify-center shrink-0`}>
          {option.optionNumber}
        </span>
        <span className={`text-xs font-semibold ${color.text} flex-1`}>{option.optionLabel}</span>
        {option.keywords.length > 0 && (
          <div className="flex gap-1">
            {option.keywords.slice(0, 2).map((kw) => (
              <span key={kw} className="text-[9px] bg-white/70 border border-gray-200 text-gray-500 px-1.5 py-0.5 rounded-full font-mono">
                &quot;{kw}&quot;
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Arrow */}
      <div className="flex justify-center py-1">
        <ArrowDown size={12} className="text-gray-400" />
      </div>

      {/* Drop zone */}
      <div
        className={`mx-3 mb-3 min-h-[64px] rounded-xl border-2 border-dashed transition-all duration-150 flex items-center justify-center ${
          isOver
            ? 'border-[#0a2d4d] bg-[#0a2d4d]/5 scale-[1.01]'
            : action
            ? 'border-transparent bg-transparent' :'border-gray-200 bg-white/60'
        }`}
      >
        {action ? (
          <div className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border ${action.bgColor} ${action.borderColor}`}>
            <action.icon size={14} className={action.textColor} />
            <div className="flex-1 min-w-0">
              <p className={`text-[11px] font-semibold ${action.textColor}`}>{action.label}</p>
              {action.type !== 'no_action' && (
                <input
                  type="text"
                  value={action.value}
                  onChange={(e) => onUpdateValue(option.id, e.target.value)}
                  placeholder={action.type === 'enroll_campaign' ? 'Campaign name…' : 'Template name…'}
                  className="mt-1 w-full text-[11px] bg-white/80 border border-gray-200 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#0a2d4d]/30"
                  onClick={(e) => e.stopPropagation()}
                />
              )}
            </div>
            <button
              onClick={() => onRemoveAction(option.id)}
              className="p-1 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors shrink-0"
            >
              <X size={12} />
            </button>
          </div>
        ) : (
          <p className="text-[11px] text-gray-400 text-center px-3">
            {isOver ? '✦ Drop here' : 'Drag an action block here'}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Droppable Wrapper ────────────────────────────────────────────────────────
function DroppableOptionSlot({
  option,
  index,
  onRemoveAction,
  onUpdateValue,
}: {
  option: FlowOption;
  index: number;
  onRemoveAction: (optionId: string) => void;
  onUpdateValue: (optionId: string, value: string) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `slot-${option.id}` });

  return (
    <div ref={setNodeRef}>
      <OptionDropZone
        option={option}
        index={index}
        onRemoveAction={onRemoveAction}
        onUpdateValue={onUpdateValue}
        isOver={isOver}
      />
    </div>
  );
}

// ─── Drag Overlay Preview ─────────────────────────────────────────────────────
function DragOverlayBlock({ block }: { block: ActionBlock }) {
  const Icon = block.icon;
  return (
    <div className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border-2 shadow-xl ${block.bgColor} ${block.borderColor} cursor-grabbing`}>
      <GripVertical size={13} className="text-gray-400" />
      <Icon size={15} className={block.textColor} />
      <span className={`text-xs font-semibold ${block.textColor}`}>{block.label}</span>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function DragDropFlowBuilder({ onClose, onApply }: DragDropFlowBuilderProps) {
  const [flowOptions, setFlowOptions] = useState<FlowOption[]>([
    { id: 'opt-1', optionNumber: 1, optionLabel: 'Option 1', keywords: ['1'], assignedAction: null },
    { id: 'opt-2', optionNumber: 2, optionLabel: 'Option 2', keywords: ['2'], assignedAction: null },
  ]);
  const [activeBlock, setActiveBlock] = useState<ActionBlock | null>(null);
  const [newKeyword, setNewKeyword] = useState<Record<string, string>>({});
  const [editingLabel, setEditingLabel] = useState<string | null>(null);
  const nextId = useRef(3);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // ── Drag handlers ──
  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current as { type: string; block: ActionBlock } | undefined;
    if (data?.type === 'palette') {
      setActiveBlock(data.block);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveBlock(null);
    const { active, over } = event;
    if (!over) return;

    const dragData = active.data.current as { type: string; block: ActionBlock } | undefined;
    if (!dragData || dragData.type !== 'palette') return;

    const overId = String(over.id);
    if (!overId.startsWith('slot-')) return;

    const optionId = overId.replace('slot-', '');
    const block = dragData.block;

    setFlowOptions((prev) =>
      prev.map((opt) =>
        opt.id === optionId
          ? {
              ...opt,
              assignedAction: {
                ...block,
                id: `${block.id}-${Date.now()}`,
                value: '',
              },
            }
          : opt
      )
    );
  };

  const handleDragOver = (_event: DragOverEvent) => {};

  // ── Option management ──
  const addOption = () => {
    const num = nextId.current++;
    setFlowOptions((prev) => [
      ...prev,
      {
        id: `opt-${num}`,
        optionNumber: prev.length + 1,
        optionLabel: `Option ${prev.length + 1}`,
        keywords: [String(prev.length + 1)],
        assignedAction: null,
      },
    ]);
  };

  const removeOption = (optionId: string) => {
    setFlowOptions((prev) => {
      const updated = prev.filter((o) => o.id !== optionId).map((o, i) => ({ ...o, optionNumber: i + 1 }));
      return updated;
    });
  };

  const removeAction = (optionId: string) => {
    setFlowOptions((prev) =>
      prev.map((o) => (o.id === optionId ? { ...o, assignedAction: null } : o))
    );
  };

  const updateActionValue = (optionId: string, value: string) => {
    setFlowOptions((prev) =>
      prev.map((o) =>
        o.id === optionId && o.assignedAction
          ? { ...o, assignedAction: { ...o.assignedAction, value } }
          : o
      )
    );
  };

  const updateOptionLabel = (optionId: string, label: string) => {
    setFlowOptions((prev) =>
      prev.map((o) => (o.id === optionId ? { ...o, optionLabel: label } : o))
    );
  };

  const addKeyword = (optionId: string) => {
    const kw = (newKeyword[optionId] ?? '').trim().toLowerCase();
    if (!kw) return;
    setFlowOptions((prev) =>
      prev.map((o) =>
        o.id === optionId && !o.keywords.includes(kw)
          ? { ...o, keywords: [...o.keywords, kw] }
          : o
      )
    );
    setNewKeyword((prev) => ({ ...prev, [optionId]: '' }));
  };

  const removeKeyword = (optionId: string, kw: string) => {
    setFlowOptions((prev) =>
      prev.map((o) =>
        o.id === optionId ? { ...o, keywords: o.keywords.filter((k) => k !== kw) } : o
      )
    );
  };

  // ── Apply ──
  const handleApply = () => {
    const mapped = flowOptions.map((o) => ({
      option_number: o.optionNumber,
      option_label: o.optionLabel,
      action_type: (o.assignedAction?.type === 'no_action' ? 'none' : (o.assignedAction?.type ?? 'none')) as 'enroll_campaign' | 'send_template' | 'none',
      campaign_name: o.assignedAction?.type === 'enroll_campaign' ? o.assignedAction.value : '',
      template_name: o.assignedAction?.type === 'send_template' ? o.assignedAction.value : '',
      keywords: o.keywords,
    }));
    onApply(mapped);
  };

  const allAssigned = flowOptions.every((o) => o.assignedAction !== null);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-[#0a2d4d] to-[#0d3a63] text-white shrink-0">
          <div>
            <h2 className="text-base font-bold tracking-tight">Flow Builder — Drag & Drop</h2>
            <p className="text-xs text-white/60 mt-0.5">Drag action blocks onto option slots to build your list picker flow</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
          >
            {/* Left: Palette */}
            <div className="w-52 shrink-0 border-r border-gray-100 bg-gray-50 p-4 flex flex-col gap-3 overflow-y-auto">
              <p className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">Action Blocks</p>
              <p className="text-[11px] text-gray-400 leading-relaxed -mt-1">Drag any block onto an option slot on the right</p>
              <div className="flex flex-col gap-2 mt-1">
                {PALETTE_BLOCKS.map((block) => (
                  <DraggablePaletteBlock key={block.id} block={block} />
                ))}
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-2">Legend</p>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 text-[11px] text-gray-500">
                    <div className="w-3 h-3 rounded-full bg-blue-500 shrink-0" />
                    Enroll into campaign
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-gray-500">
                    <div className="w-3 h-3 rounded-full bg-emerald-500 shrink-0" />
                    Send template message
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-gray-500">
                    <div className="w-3 h-3 rounded-full bg-gray-400 shrink-0" />
                    No action taken
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Flow canvas */}
            <div className="flex-1 overflow-y-auto p-5">
              {/* Trigger node */}
              <div className="flex justify-center mb-4">
                <div className="flex items-center gap-2 bg-[#0a2d4d] text-white text-xs font-semibold px-4 py-2.5 rounded-xl shadow-sm">
                  <MessageSquare size={13} />
                  Lead replies to List Picker
                </div>
              </div>
              <div className="flex justify-center mb-4">
                <ArrowDown size={14} className="text-gray-400" />
              </div>

              {/* Option slots grid */}
              <div className={`grid gap-3 ${flowOptions.length === 1 ? 'grid-cols-1 max-w-xs mx-auto' : flowOptions.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                {flowOptions.map((option, index) => (
                  <div key={option.id} className="flex flex-col gap-1.5">
                    {/* Editable label row */}
                    <div className="flex items-center gap-1.5">
                      {editingLabel === option.id ? (
                        <input
                          autoFocus
                          type="text"
                          value={option.optionLabel}
                          onChange={(e) => updateOptionLabel(option.id, e.target.value)}
                          onBlur={() => setEditingLabel(null)}
                          onKeyDown={(e) => { if (e.key === 'Enter') setEditingLabel(null); }}
                          className="flex-1 text-xs border border-[#0a2d4d]/30 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#0a2d4d]/20"
                        />
                      ) : (
                        <button
                          onClick={() => setEditingLabel(option.id)}
                          className="flex-1 text-left text-xs text-gray-500 hover:text-[#0a2d4d] truncate px-1"
                          title="Click to rename"
                        >
                          ✏️ {option.optionLabel}
                        </button>
                      )}
                      {flowOptions.length > 1 && (
                        <button
                          onClick={() => removeOption(option.id)}
                          className="p-1 rounded text-gray-300 hover:text-red-400 transition-colors"
                        >
                          <Trash2 size={11} />
                        </button>
                      )}
                    </div>

                    {/* Drop slot */}
                    <DroppableOptionSlot
                      option={option}
                      index={index}
                      onRemoveAction={removeAction}
                      onUpdateValue={updateActionValue}
                    />

                    {/* Keywords */}
                    <div className="bg-gray-50 border border-gray-100 rounded-xl px-2.5 py-2">
                      <p className="text-[10px] font-semibold text-gray-400 mb-1.5 flex items-center gap-1">
                        <Tag size={9} /> Keywords
                      </p>
                      <div className="flex flex-wrap gap-1 mb-1.5">
                        {option.keywords.map((kw) => (
                          <span key={kw} className="inline-flex items-center gap-0.5 bg-white border border-gray-200 text-gray-600 text-[10px] px-1.5 py-0.5 rounded-full">
                            {kw}
                            <button onClick={() => removeKeyword(option.id, kw)} className="text-gray-400 hover:text-red-400 ml-0.5">×</button>
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-1">
                        <input
                          type="text"
                          value={newKeyword[option.id] ?? ''}
                          onChange={(e) => setNewKeyword((prev) => ({ ...prev, [option.id]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(option.id); } }}
                          placeholder="Add keyword…"
                          className="flex-1 text-[10px] border border-gray-200 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#0a2d4d]/20"
                        />
                        <button
                          onClick={() => addKeyword(option.id)}
                          className="px-2 py-1 text-[10px] bg-gray-200 text-gray-600 rounded-md hover:bg-gray-300 transition-colors"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Add option */}
              {flowOptions.length < 5 && (
                <button
                  onClick={addOption}
                  className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-dashed border-gray-200 rounded-xl text-xs text-gray-400 hover:border-[#0a2d4d]/30 hover:text-[#0a2d4d] hover:bg-[#0a2d4d]/5 transition-all"
                >
                  <Plus size={13} />
                  Add Option Slot
                </button>
              )}
            </div>

            <DragOverlay>
              {activeBlock ? <DragOverlayBlock block={activeBlock} /> : null}
            </DragOverlay>
          </DndContext>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50 shrink-0">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            {allAssigned ? (
              <>
                <CheckCircle size={14} className="text-emerald-500" />
                <span className="text-emerald-600 font-medium">All options have actions assigned</span>
              </>
            ) : (
              <>
                <Info size={14} className="text-amber-500" />
                <span>{flowOptions.filter((o) => !o.assignedAction).length} option(s) without action</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-[#0a2d4d] text-white rounded-lg hover:bg-[#0d3a63] transition-colors shadow-sm"
            >
              <CheckCircle size={14} />
              Apply to Config
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
